'use client'

import { X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  InfoBanner,
  InputSelect,
  InputTextArea,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { useDropOffPointMapContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { ErrorState } from '@marketplace-web/error-display/error-display-feature'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  DistanceUnitEnum,
  getTransactionCancellationReasons,
  transformTransactionCancellationReasonsDto,
  TransactionCancellationReasonModel,
  clickEvent,
  selfServiceViewScreenEvent,
  cancelTransaction,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useLoadGoogleApi } from '@marketplace-web/shipping/google-maps-feature'
import {
  getDefaultShippingAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'

import { Field, FormModel } from './types'
import useRequiredFormField from './useRequiredFormField'

const USER_EXPLANATION_MAX_LENGTH = 100

type Props = {
  show: boolean
  transactionId: number
  isBuyer: boolean
  onSuccess: () => void
  onCancel: () => void
  onDropOffPointModalOpen: () => void
}

const CancelTransactionModal = ({
  show,
  transactionId,
  onSuccess,
  onCancel,
  onDropOffPointModalOpen,
  isBuyer,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.conversation_info.cancel_transaction_modal')
  const { fetchNearbyDropOffPoints, selectedDropOffPoint } = useDropOffPointMapContext()

  const [uiState, setUiState] = useState<UiState>(UiState.Idle)
  const [reasons, setReasons] = useState<Array<TransactionCancellationReasonModel>>([])

  const { fetch: fetchDefaultShippingAddress, transformedData: defaultShippingAddress } = useFetch(
    getDefaultShippingAddress,
    transformDefaultShippingAddress,
  )

  const formMethods = useForm<FormModel>({
    defaultValues: { [Field.Reason]: '' },
  })

  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = formMethods

  const validationMessage = useFormValidationMessage(errors)
  const requiredField = useRequiredFormField()

  const selectedReasonId = watch<Field.Reason>(Field.Reason)

  const isGoogleApiLoaded = useLoadGoogleApi()

  useEffect(() => {
    if (!show) return

    track(
      selfServiceViewScreenEvent({
        screen: 'transaction_cancellation_reasons',
        transactionId,
      }),
    )
  }, [show, track, transactionId])

  useEffect(() => {
    async function fetchTransactionCancellationReasons() {
      setUiState(UiState.Pending)

      const response = await getTransactionCancellationReasons(transactionId)

      if ('errors' in response) {
        setUiState(UiState.Failure)

        return
      }

      const transformedReasons = transformTransactionCancellationReasonsDto(response.reasons)

      setReasons(transformedReasons)
      setUiState(UiState.Success)
    }

    if (!show) return

    fetchTransactionCancellationReasons()
  }, [show, transactionId])

  useEffect(() => {
    if (defaultShippingAddress) return

    if (show) fetchDefaultShippingAddress()
  }, [defaultShippingAddress, fetchDefaultShippingAddress, show])

  useEffect(() => {
    if (!isGoogleApiLoaded || !defaultShippingAddress || isBuyer) return

    if (show) {
      fetchNearbyDropOffPoints({ address: defaultShippingAddress })
    }
  }, [fetchNearbyDropOffPoints, defaultShippingAddress, show, isGoogleApiLoaded, isBuyer])

  const handleFormSubmit = handleSubmit(async values => {
    const { reason, userExplanation } = values

    const response = await cancelTransaction({
      id: transactionId,
      reasonId: Number(reason),
      userExplanation,
    })

    if ('errors' in response) {
      setUiState(UiState.Failure)

      return
    }

    onSuccess()
  })

  function renderReason() {
    const options = reasons.map(reason => ({ value: reason.id, label: reason.reason }))

    return (
      <Controller
        name={Field.Reason}
        render={({ field }) => (
          <InputSelect
            {...field}
            options={options}
            title={translate('fields.reason.title')}
            placeholder={translate('fields.reason.placeholder')}
            required
            validation={renderValidation(validationMessage(Field.Reason))}
          />
        )}
        control={control}
        rules={{
          required: requiredField(Field.Reason),
        }}
      />
    )
  }

  function getDistanceToNearestDropOffPoint() {
    if (!selectedDropOffPoint) return null

    const { distance, distanceUnit } = selectedDropOffPoint

    if (!distanceUnit) return null
    if (!distance && distance !== 0) return null

    if (distanceUnit === DistanceUnitEnum.Kilometers || distanceUnit === DistanceUnitEnum.Miles) {
      const roundedDistance = distance.toFixed(1)

      return `${roundedDistance} ${distanceUnit}`
    }

    const roundedDistance = Math.round(distance)

    return `${roundedDistance} ${distanceUnit}`
  }

  function trackDropOffPointModalOpenClick() {
    const targetDetails = {
      transaction_id: transactionId,
    }

    track(
      clickEvent({
        target: 'link_dropoff_map',
        screen: 'transaction_cancellation_reasons',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function handleDropOffPointModalOpen() {
    trackDropOffPointModalOpenClick()

    onDropOffPointModalOpen()
  }

  function renderNearestDropOffPoint() {
    if (!selectedReasonId) return null

    const reason = reasons.find(({ id }) => id === Number(selectedReasonId))

    if (!reason?.showNearestDropOffPoint) return null
    if (!selectedDropOffPoint) return null

    return (
      <InfoBanner
        body={
          <Text
            as="span"
            text={translate('nearest_drop_off_point', {
              distance: getDistanceToNearestDropOffPoint(),
              'see-drop-off-points': chunks => (
                <button type="button" onClick={handleDropOffPointModalOpen}>
                  <Text clickable underline text={chunks} as="span" />
                </button>
              ),
            })}
            html
          />
        }
        type={InfoBanner.Type.Info}
        testId="cancel-transaction-modal-nearest-drop-off-point"
      />
    )
  }

  function renderUserExplanation() {
    if (!selectedReasonId) return null

    const reason = reasons.find(({ id }) => id === Number(selectedReasonId))

    if (!reason?.hasInput) return null

    return (
      <Controller
        name={Field.UserExplanation}
        render={({ field }) => (
          <InputTextArea
            {...field}
            rows={4}
            maxLength={USER_EXPLANATION_MAX_LENGTH}
            validation={renderValidation(validationMessage(Field.UserExplanation))}
            title={translate('fields.user_explanation.title')}
          />
        )}
        defaultValue={null}
        rules={{
          required: requiredField(Field.UserExplanation),
        }}
      />
    )
  }

  function renderContent() {
    return (
      <FormProvider {...formMethods}>
        <Cell body={translate('body')} />
        {renderReason()}
        {renderNearestDropOffPoint()}
        {renderUserExplanation()}
      </FormProvider>
    )
  }

  function renderLoader() {
    return <ContentLoader styling={ContentLoader.Styling.Wide} />
  }

  function renderActions() {
    return (
      <Cell>
        <Button
          disabled={uiState === UiState.Pending || isSubmitting}
          text={translate('buttons.cancel_this_order')}
          styling={Button.Styling.Filled}
          theme="warning"
          onClick={handleFormSubmit}
          testId="cancel-transaction-modal-cancel-this-order"
        />
        <Spacer size={Spacer.Size.Large} />
        <Button
          text={translate('buttons.no_go_back')}
          styling={Button.Styling.Flat}
          theme="warning"
          onClick={onCancel}
          testId="cancel-transaction-modal-no-go-back"
        />
      </Cell>
    )
  }

  function renderModalContent() {
    return (
      <>
        {uiState === UiState.Pending ? renderLoader() : renderContent()}
        {renderActions()}
      </>
    )
  }

  function renderError() {
    return <ErrorState />
  }

  return (
    <Dialog show={show} testId="cancel-transaction-modal">
      <Navigation
        body={translate('title')}
        right={
          <Button
            styling={Button.Styling.Flat}
            onClick={onCancel}
            iconName={X24}
            inline
            testId="cancel-transaction-modal-close"
          />
        }
      />
      <Divider />
      {uiState === UiState.Failure ? renderError() : renderModalContent()}
    </Dialog>
  )
}
export default CancelTransactionModal
